import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Button } from './Button.tsx';
import { Dialog } from './Dialog.tsx';
import { NoticesContext, type Notice, type NoticesApi } from './notices-context.ts';

// Cola de avisos modales (§12): "Información" / "Atención" con botón "Entendido".
// Los errores tienen prioridad y un mensaje idéntico pendiente no se duplica.
export function NoticesProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Notice[]>([]);

  const push = useCallback((notice: Notice) => {
    setQueue((current) => {
      if (current.some((n) => n.kind === notice.kind && n.message === notice.message)) return current;
      const next = [...current, notice];
      return next.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'error' ? -1 : 1));
    });
  }, []);

  const api = useMemo<NoticesApi>(
    () => ({
      info: (message, details) => push(details ? { kind: 'info', message, details } : { kind: 'info', message }),
      error: (message, details) => push(details ? { kind: 'error', message, details } : { kind: 'error', message }),
    }),
    [push],
  );

  const current = queue[0];
  const dismiss = () => setQueue((q) => q.slice(1));

  return (
    <NoticesContext.Provider value={api}>
      {children}
      <Dialog
        open={Boolean(current)}
        alert={current?.kind === 'error'}
        title={current?.kind === 'error' ? 'Atención' : 'Información'}
        onClose={dismiss}
        footer={
          <Button onClick={dismiss} autoFocus>
            Entendido
          </Button>
        }
      >
        <p>{current?.message}</p>
        {current?.details?.length ? (
          <ul className="notice-details">
            {current.details.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        ) : null}
      </Dialog>
    </NoticesContext.Provider>
  );
}
